import {useState} from 'react';
import Table from 'react-bootstrap/Table';
import {getDivision} from '../common/rating';
import {orderBy, deepOrderBy} from '../common/utils';

const playerRatingDiff = (ratingDiff, ratingDate) => {
  if (!ratingDiff || ratingDiff === 0) return '';
  return ratingDiff > 0 ? (
    <span style={{color: '#0F0'}} title={ratingDate}>
      +{ratingDiff}
    </span>
  ) : (
    <span style={{color: '#F00'}} title={ratingDate}>
      {ratingDiff}
    </span>
  );
};

const playerRatingImprovement = (player) => {
  const diff = player.rating - player.ratingDetails.firstRating;
  if (diff === 0) return '';
  return diff > 0 ? (
    <span style={{color: '#0F0'}}>&uarr;{diff}</span>
  ) : (
    <span style={{color: '#F00'}}>&darr;{diff}</span>
  );
};

/* const playerRatingChange = (ratingDate, recentRatingChange) => {
  return recentRatingChange ? (
    <></>
  ) : (
    <span style={{backgroundColor: '#ff0', color: '#000'}} title={ratingDate}>OLD</span>
  );
}; */

const isCertified = (player) => {
  return player.isCertifiedOfficial && <span title="Certified Official">&#10004;</span>;
};

const PlayerRow = (player) => {
  const profileUrl = `https://www.pdga.com/player/${player.pdga}`;
  const roundsUsed = `${player.ratingDetails.roundsUsed} rounds used`;
  const wins = player.tournamentsWon.map((tw) => `${tw.tournament} (${tw.division})`).join(', ');
  return (
    <tr>
      <td>
        {player.name} {isCertified(player)}
      </td>
      <td title={roundsUsed}>
        {player.rating}{' '}
        {player.recentRatingChange && playerRatingDiff(player.ratingDiff, player.ratingDate)}
      </td>
      <td>
        <a href={profileUrl}>#{player.pdga}</a>
      </td>
      <td>{getDivision(player.rating, player.isClassifiedAsPro, player.isFemale)}</td>
      <td>
        {player.ratingDetails.highestRating} (&rarr;{' '}
        {getDivision(player.ratingDetails.highestRating, player.isClassifiedAsPro, player.isFemale)}
        )
      </td>
      <td>
        {player.ratingDetails.lowestRating} (&rarr;{' '}
        {getDivision(player.ratingDetails.lowestRating, player.isClassifiedAsPro, player.isFemale)})
      </td>
      <td>
        {player.ratingDetails.highestRatedRound} / {player.ratingDetails.lowestRatedRound}
      </td>
      <td>{player.tournaments}</td>
      <td title={wins}>{player.wins}</td>
      <td>
        {player.ratingDetails.firstRating} {playerRatingImprovement(player)}
      </td>
    </tr>
  );
};

const DEFAULT_ORDER = {
  fields: ['rating', 'pdga'],
  orders: ['desc', 'asc']
};

const orderPlayersByRatings = (playerList) => {
  return orderBy(playerList, DEFAULT_ORDER.fields, DEFAULT_ORDER.orders);
};

export function PlayerTable(props) {
  const [orderedPlayers, setPlayers] = useState(orderPlayersByRatings(props.data));
  const [sortingOrders] = useState({
    rating: 'desc',
    pdga: 'asc',
    'ratingDetails.highestRating': 'desc',
    'ratingDetails.lowestRating': 'desc',
    'ratingDetails.highestRatedRound': 'desc',
    'ratingDetails.lowestRatedRound': 'desc',
    tournaments: 'desc',
    wins: 'desc',
    'ratingDetails.firstRating': 'desc'
  });

  const sortPlayers = (playerList, field) => {
    if (sortingOrders[field]) {
      sortingOrders[field] = sortingOrders[field] === 'asc' ? 'desc' : 'asc';
    }
    let list = [];

    if (field.includes('.')) {
      const [parentField, childField] = field.split('.');
      list = deepOrderBy(playerList, parentField, [childField], [sortingOrders[field]]);
    } else {
      list = orderBy(
        playerList,
        [field, ...DEFAULT_ORDER.fields],
        [sortingOrders[field], ...DEFAULT_ORDER.orders]
      );
    }

    setPlayers(list);
  };

  const getSortOrder = (field) => {
    switch (sortingOrders[field]) {
      case 'asc':
        return 'asc'; //'&darr;';
      case 'desc':
        return 'desc'; //'&uarr;';
      default:
        return '';
    }
  };

  return (
    <Table striped bordered hover responsive variant="dark">
      <thead className="player-table-headers">
        <tr>
          <th>
            Player
            <br />
            (&#10004;: certified official)
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'rating')}>
            Rating <small className="sorting-order">{getSortOrder('rating')}</small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'pdga')}>
            PDGA Profile <small className="sorting-order">{getSortOrder('pdga')}</small>
          </th>
          <th>
            Recommended
            <br /> Division
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'ratingDetails.highestRating')}>
            Highest Rating{' '}
            <small className="sorting-order">{getSortOrder('ratingDetails.highestRating')}</small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'ratingDetails.highestRating')}>
            Lowest Rating{' '}
            <small className="sorting-order">{getSortOrder('ratingDetails.highestRating')}</small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'ratingDetails.highestRatedRound')}>
            Highest/Lowest Rated Round{' '}
            <small className="sorting-order">
              {getSortOrder('ratingDetails.highestRatedRound')}
            </small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'tournaments')}>
            # Of Tournaments<small className="sorting-order">{getSortOrder('tournaments')}</small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'wins')}>
            Tournaments Wins <small className="sorting-order">{getSortOrder('wins')}</small>
          </th>
          <th onClick={() => sortPlayers(orderedPlayers, 'ratingDetails.firstRating')}>
            1<sup>st</sup> Rating{' '}
            <small className="sorting-order">{getSortOrder('ratingDetails.firstRating')}</small>
          </th>
        </tr>
      </thead>
      <tbody>
        {orderedPlayers.map((player) => (
          <PlayerRow key={player.pdga} {...player} />
        ))}
      </tbody>
    </Table>
  );
}
